import type { MediaItem } from "@/types/media";

export type SortKey = "latest" | "rating" | "popularity";

export interface MediaFilters {
  type?: "movie" | "tv";
  region?: string;
  year?: string;
  genre?: string;
  sort?: SortKey;
  keyword?: string;
}

export const REGIONS = ["全部", "中国大陆", "中国香港", "美国", "英国", "日本", "韩国", "法国"];
export const YEARS = ["全部", "2024", "2023", "2022"];
export const GENRES = [
  "全部",
  "剧情",
  "科幻",
  "悬疑",
  "犯罪",
  "爱情",
  "动作",
  "冒险",
  "历史",
  "都市",
  "青春",
  "家庭",
];

export const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新" },
  { key: "rating", label: "评分" },
  { key: "popularity", label: "热度" },
];

export function sortMedia(items: MediaItem[], sort: SortKey = "latest"): MediaItem[] {
  const cloned = [...items];
  switch (sort) {
    case "rating":
      return cloned.sort((a, b) => b.rating - a.rating);
    case "popularity":
      return cloned.sort((a, b) => b.popularity - a.popularity);
    case "latest":
    default:
      return cloned.sort(
        (a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()
      );
  }
}

export function filterMedia(items: MediaItem[], filters: MediaFilters = {}): MediaItem[] {
  let result = items;

  if (filters.type) {
    result = result.filter((item) => item.type === filters.type);
  }
  if (filters.region && filters.region !== "全部") {
    result = result.filter((item) => item.region === filters.region);
  }
  if (filters.year && filters.year !== "全部") {
    result = result.filter((item) => String(item.year) === filters.year);
  }
  if (filters.genre && filters.genre !== "全部") {
    result = result.filter((item) => item.genres.includes(filters.genre));
  }
  if (filters.keyword && filters.keyword.trim()) {
    const kw = filters.keyword.trim().toLowerCase();
    result = result.filter((item) => {
      const haystack = [
        item.title,
        item.region,
        item.director,
        item.synopsis,
        item.genres.join(" "),
        item.type === "movie" ? "电影" : "电视剧",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(kw);
    });
  }

  return sortMedia(result, filters.sort);
}

export function paginate<T>(items: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

export function totalPages(count: number, pageSize: number): number {
  return Math.max(1, Math.ceil(count / pageSize));
}

export const formatPopularity = (value: number): string => {
  if (value >= 10000) return `${(value / 10000).toFixed(1)}万`;
  return String(value);
};